import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { Button, Card, CardBody, CardHeader, Field, Textarea, Toggle, useToast } from '@/components/ui';

const hostsToText = (hosts: string[]) => hosts.join('\n');
const textToHosts = (text: string) =>
  Array.from(new Set(text.split(/[\s,]+/).map((h) => h.trim().toLowerCase()).filter(Boolean)));

/** A labelled switch row (the Toggle primitive itself renders only the switch). */
function ToggleRow({ checked, onChange, disabled, label, description }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; label: string; description: ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <Toggle checked={checked} onChange={onChange} disabled={disabled} label={label} />
      <div className="min-w-0">
        <div className="text-sm font-medium text-fg">{label}</div>
        <p className="mt-0.5 text-xs text-fg-muted">{description}</p>
      </div>
    </div>
  );
}

/**
 * The project's security authorization Scope: which hosts scans may touch, whether production is allowed, the rules of
 * engagement, and the standing authorization. No scan runs until a Project Admin lists the app's host and confirms
 * authorization. Everyone sees it; only `canManage` (Project Admin) edits it.
 */
export function SecurityScopeCard({ projectId, canManage, suggestHost }: { projectId: string; canManage: boolean; suggestHost?: string | null }) {
  const qc = useQueryClient();
  const toast = useToast();
  const scope = useQuery({ queryKey: ['security-scope', projectId], queryFn: () => api.securityScope(projectId) });

  const [hosts, setHosts] = useState('');
  const [allowProduction, setAllowProduction] = useState(false);
  const [roe, setRoe] = useState('');
  const [attested, setAttested] = useState(false);

  useEffect(() => {
    if (!scope.data) return;
    setHosts(hostsToText(scope.data.authorizedHosts.length ? scope.data.authorizedHosts : suggestHost ? [suggestHost] : []));
    setAllowProduction(scope.data.allowProduction);
    setRoe(scope.data.rulesOfEngagement);
    setAttested(Boolean(scope.data.attestation));
  }, [scope.data, suggestHost]);

  const save = useMutation({
    mutationFn: () => api.updateSecurityScope(projectId, { authorizedHosts: textToHosts(hosts), allowProduction, rulesOfEngagement: roe, attested }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['security-scope', projectId] });
      toast.success('Security scope saved');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const att = scope.data?.attestation ?? null;

  return (
    <Card>
      <CardHeader title="Security scope" description="Confirm which app this project is allowed to scan. Scans run only against these hosts." />
      <CardBody className="space-y-4">
        <Field label="Authorized hosts" hint="One host per line. A leading *. covers sub-domains, e.g. *.staging.example.com. Use staging or test hosts.">
          {(id) => <Textarea id={id} rows={3} value={hosts} onChange={(e) => setHosts(e.target.value)} disabled={!canManage} placeholder="staging.example.com" spellCheck={false} />}
        </Field>
        <Field label="Rules of engagement" hint="Optional. Passed to the scanner, e.g. “do not touch /admin, no password resets”.">
          {(id) => <Textarea id={id} rows={2} value={roe} onChange={(e) => setRoe(e.target.value)} disabled={!canManage} spellCheck={false} />}
        </Field>
        <ToggleRow
          checked={allowProduction}
          onChange={setAllowProduction}
          disabled={!canManage}
          label="Allow production hosts"
          description="Off by default. Scanning runs real requests and can change data — keep this off unless you mean to scan production."
        />
        <div className="rounded-field bg-warning-subtle p-3 ring-1 ring-warning/40 ring-inset">
          <ToggleRow
            checked={attested}
            onChange={setAttested}
            disabled={!canManage}
            label="I am authorized to test these hosts"
            description="Required before any scan can run. Only confirm this for applications your organization owns or has written permission to test."
          />
          {att && (
            <p className="mt-2 text-xs text-fg-muted">
              Confirmed by {att.byName} on {formatDateTime(att.at)}.
            </p>
          )}
        </div>
        {canManage && (
          <div className="flex justify-end">
            <Button onClick={() => save.mutate()} loading={save.isPending} disabled={scope.isLoading}>
              Save scope
            </Button>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
